<script setup>
import { computed } from 'vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import { formatDate, lardoRemarkParts, lardoSeverity } from '@/constants/risk';

// One LARDO alert: who, what was found, the recommended step and whether
// an action has been recorded.
const props = defineProps({
  alert: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });

const severity = computed(() => lardoSeverity(props.alert?.remarks));
const parts = computed(() => lardoRemarkParts(props.alert?.remarks));
</script>

<template>
  <UiDialog
    v-model="open"
    :title="alert?.name || 'LARDO alert'"
    :description="alert?.lrn ? `LRN ${alert.lrn}` : ''"
    size="md"
    :persistent="false"
  >
    <div v-if="alert" class="details">
      <div class="details__badges">
        <UiBadge :variant="severity.variant">
          <v-icon size="14">{{ severity.icon }}</v-icon>
          {{ severity.label }}
        </UiBadge>
        <UiBadge :variant="alert.pending ? 'outline' : 'green'">
          {{ alert.pending ? 'Pending action' : 'Action recorded' }}
        </UiBadge>
      </div>

      <dl class="facts">
        <div>
          <dt>Grade and section</dt>
          <dd>{{ [alert.grade_level, alert.room_name].filter(Boolean).join(' · ') || '—' }}</dd>
        </div>
        <div>
          <dt>Subject</dt>
          <dd>{{ alert.subject_title || '—' }}</dd>
        </div>
        <div>
          <dt>Adviser</dt>
          <dd>{{ alert.adviser || '—' }}</dd>
        </div>
        <div>
          <dt>Flagged on</dt>
          <dd>{{ formatDate(alert.created_at) }}</dd>
        </div>
      </dl>

      <section class="note">
        <h3>What happened</h3>
        <p>{{ parts.finding || '—' }}</p>
      </section>
      <section v-if="parts.recommendation" class="note">
        <h3>Recommended action</h3>
        <p>{{ parts.recommendation }}</p>
      </section>
      <section v-if="alert.action_taken" class="note note--done">
        <h3>Action taken</h3>
        <p>{{ alert.action_taken }}</p>
      </section>
    </div>

    <template #footer>
      <v-btn variant="outlined" @click="open = false">Close</v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.details__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
}
.facts dt {
  font-size: 12px;
  color: var(--muted-foreground);
}
.facts dd {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 500;
}
.note {
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--subtle);
}
.note--done {
  border-color: color-mix(in srgb, #10b981 35%, transparent);
  background: color-mix(in srgb, #10b981 8%, transparent);
}
.note h3 {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
}
.note p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--secondary-foreground);
}
@media (max-width: 600px) {
  .facts {
    grid-template-columns: 1fr;
  }
}
</style>
